import type { Router, RouteLocationNormalized } from 'vue-router'
import AppConfig from '@/config'
import { tenant, brandName } from '@/config/tenant'
import { useAuthStore } from '@/stores/auth'
import { useLoginStore } from '@/stores/login'
import { useThreadStore } from '@/stores/thread'
import { getAuthToken } from '@/constants/storageKeys'
import { redirectToAuth } from '@/utils/authRedirect'
import { yandexMetrika } from '@/services/yandexMetrika'
import { postPromoInit } from '@/api/endpoints/promo'

declare const document: Document

const PROMO_PREFIX = '/promo/'

function rawQuery(to: RouteLocationNormalized): string {
	// Переход по внешней ссылке — единственный реальный случай: location уже стоит на /promo/...
	if (window.location.pathname === to.path) return window.location.search
	// Страховка для гипотетической навигации на промолинк изнутри приложения.
	const i = to.fullPath.indexOf('?')
	return i === -1 ? '' : to.fullPath.slice(i)
}

/** Идентификатор промолинка — первый сегмент после /promo/. Пустой означает «вызывать нечего». */
function promolinkFrom(path: string): string {
	const segment = path.slice(PROMO_PREFIX.length).split('/')[0]
	if (!segment) return ''
	try {
		return decodeURIComponent(segment)
	} catch {
		return segment // битый %-escape не должен ронять навигацию
	}
}

export function registerGuards(router: Router): void {
	router.beforeEach((to) => {
		if (AppConfig.internalB2C && to.path.startsWith(PROMO_PREFIX)) {
			const promolink = promolinkFrom(to.path)
			// Ответа не ждём: replace — SPA-навигация, отправленный запрос она не отменяет.
			// Ошибка (битый промолинк, 500, оффлайн) человека не касается — пускаем в продукт.
			if (promolink) void postPromoInit(promolink, rawQuery(to)).catch(() => {})
			return '/'
		}

		const authStore = useAuthStore()
		// На жёсткой перезагрузке websanova auth.check() может быть ещё не готов и вернуть false,
		// из-за чего внутренние маршруты редиректили на '/'. Если токен есть в localStorage —
		// считаем авторизованным; невалидный токен отвалится на первом 401 (interceptor → logout).
		const isAuth = authStore.isAuthenticated || !!getAuthToken()

		// 1. Точка входа (Home)
		if (to.meta.auth === 'entry') {
			if (isAuth || AppConfig.authMode === 'internal' || AppConfig.devSetting) return true
			redirectToAuth()
			return false // прерываем SPA-навигацию, пока браузер уходит по replace
		}

		// 2. Требуется авторизация
		if (to.meta.auth === 'internal' && !isAuth) {
			// Исключение — чат: гостю положены бесплатные запросы, поэтому он обязан
			// доходить до /thread. Что ему реально доступно, решают гейт и бэк.
			// История чатов и кабинет остаются закрытыми (хедер подменяет их на LogInModal).
			if (!(to.meta.guestAllowed && AppConfig.internalB2C)) return '/'
		}

		// 2.1. Страницы входа — только для неавторизованных
		if (to.meta.guestOnly && isAuth) return '/'

		// 2.2. Шаг ввода кода без e-mail (прямой заход или перезагрузка) — назад на ввод e-mail
		if (to.meta.requiresLoginEmail && !useLoginStore().email) return '/login'

		// 3. Ограничение по тенанту/локали (логика ИЛИ)
		if (to.meta.tenants || to.meta.locals) {
			const byTenant = to.meta.tenants?.includes(tenant) ?? false
			const byLocal = to.meta.locals?.includes(AppConfig.local) ?? false
			if (!byTenant && !byLocal) return '/'
		}

		// 4. Секретный чат
		if (to.meta.requiresSecret) {
			const threadStore = useThreadStore()
			if (!threadStore.secretMode) return '/'
		}

		// 5. Удалённый тред
		if (to.meta.checkDeletedThread) {
			const threadStore = useThreadStore()
			if (threadStore.deleteThreadArr.includes(Number(to.params.id))) return '/'
		}

		return true
	})

	const defaultTitle = brandName()
	router.afterEach((to, from) => {
		// сохраняем подавление title при переходе thread → threadId (новый чат получает id)
		if (!(to.name === 'threadId' && from.name === 'thread')) {
			document.title = to.meta.title ? String(to.meta.title) : defaultTitle
		}
		yandexMetrika.hit(to.meta.hitWithoutQuery ? to.path : to.fullPath)
	})
}
